import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { parseCustomerNameLocalesRoot, resolveCustomerLine } from '../../constants/commerceNameLocales';
import WpaLayout from './WpaLayout';

const PRIMARY = '#4CAF50';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** 後端 deliveryDays: { mon, tue, ... holiday } → UI 用 'Mon'… 陣列 */
const API_DAY_KEYS = [
  { api: 'mon', ui: 'Mon' },
  { api: 'tue', ui: 'Tue' },
  { api: 'wed', ui: 'Wed' },
  { api: 'thu', ui: 'Thu' },
  { api: 'fri', ui: 'Fri' },
  { api: 'sat', ui: 'Sat' },
  { api: 'sun', ui: 'Sun' },
];

function joinNonEmptyLines(...parts) {
  return parts
    .filter((p) => p != null && String(p).trim() !== '')
    .map((p) => String(p).trim())
    .join('\n');
}

/** GET /api/wpa-commerce/account 回傳 name_1/2/3、name_locales 及地址列 */
function normalizeAccountFromApi(json, uiLocale) {
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') return null;

  const dd = raw.deliveryDays && typeof raw.deliveryDays === 'object' ? raw.deliveryDays : {};
  const deliveryDaysUi = API_DAY_KEYS.filter(({ api }) => dd[api] === true).map(({ ui }) => ui);

  const customerNo = raw.customerNo != null ? String(raw.customerNo).trim() : '';
  const branchNo = raw.branchNo != null ? String(raw.branchNo).trim() : '';
  const idCode = [customerNo, branchNo].filter(Boolean).join('-') || customerNo || null;

  const nl = parseCustomerNameLocalesRoot(raw.name_locales);
  const name1 = resolveCustomerLine(raw.name_1, nl, '1', uiLocale) || '-';
  const name2 = resolveCustomerLine(raw.name_2, nl, '2', uiLocale) || null;
  const addressEn = joinNonEmptyLines(
    raw.deliveryAddressEn1,
    raw.deliveryAddressEn2,
    raw.deliveryAddressEn3,
  );
  const addressCn = joinNonEmptyLines(
    raw.deliveryAddressCn1,
    raw.deliveryAddressCn2,
    raw.deliveryAddressCn3,
  );

  return {
    id: raw.id,
    idCode: idCode || '-',
    name1,
    name2,
    addressEn: addressEn || '-',
    addressCn: addressCn || '-',
    salesmanName: (raw.salesmanName && String(raw.salesmanName).trim()) || '-',
    salesmanPhone: (raw.salesmanWhatsapp && String(raw.salesmanWhatsapp).trim()) || '',
    deliveryDays: deliveryDaysUi,
    holiday: dd.holiday === true,
  };
}

const styles = {
  card: {
    margin: 16,
    padding: 16,
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#888',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  fieldLabel: {
    fontSize: 14,
    color: '#666',
    flexShrink: 0,
    marginRight: 12,
  },
  fieldValue: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  daysRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    padding: '10px 0',
  },
  dayChip: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
  },
  dayActive: {
    background: PRIMARY,
    color: '#fff',
  },
  dayInactive: {
    background: '#f0f0f0',
    color: '#ccc',
  },
  holidayChip: {
    padding: '6px 14px',
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 600,
  },
  salesmanRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
  },
  salesmanAvatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: '#e8f5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    flexShrink: 0,
  },
  salesmanInfo: {
    flex: 1,
  },
  salesmanName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
  },
  salesmanPhone: {
    fontSize: 13,
    color: '#999',
    marginTop: 2,
  },
  loading: {
    textAlign: 'center',
    padding: 48,
    color: '#999',
  },
};

export default function WpaAccountPage() {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccount = useCallback(async () => {
    const token = localStorage.getItem('wpa_token');
    let url = '/api/wpa-commerce/account';
    try {
      const cust = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
      if (cust?.id) {
        url += `?customerId=${encodeURIComponent(cust.id)}`;
      }
    } catch {
      /* ignore */
    }
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-WPA-Locale': currentLanguage || 'zh-TC',
        },
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        setAccount(null);
        return;
      }
      setAccount(normalizeAccountFromApi(json, currentLanguage));
    } catch (err) {
      console.error('Failed to fetch account', err);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, [navigate, companyId, currentLanguage]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  if (loading) {
    return (
      <WpaLayout title={t('wpa.titles.account')}>
        <div style={styles.loading}>{t('wpa.common.loading')}</div>
      </WpaLayout>
    );
  }

  if (!account) {
    return (
      <WpaLayout title={t('wpa.titles.account')}>
        <div style={styles.loading}>{t('wpa.account.loadFailed')}</div>
      </WpaLayout>
    );
  }

  const deliveryDays = account.deliveryDays || [];
  const hasHoliday = account.holiday === true;

  const valueMultiline = { ...styles.fieldValue, ...styles.fieldValueMultiline };

  return (
    <WpaLayout title={t('wpa.titles.account')}>
      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.account.basicInfo')}</div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.account.customerNo')}</span>
          <span style={styles.fieldValue}>{account.idCode}</span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.account.name1')}</span>
          <span style={valueMultiline}>{account.name1}</span>
        </div>
        {account.name2 && (
          <div style={styles.fieldRow}>
            <span style={styles.fieldLabel}>{t('wpa.account.name2')}</span>
            <span style={valueMultiline}>{account.name2}</span>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.account.sectionDelivery')}</div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.account.addressEn')}</span>
          <span style={valueMultiline}>{account.addressEn}</span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.account.addressCn')}</span>
          <span style={valueMultiline}>{account.addressCn}</span>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.account.sectionSales')}</div>
        <div style={styles.salesmanRow}>
          <div style={styles.salesmanAvatar}>👤</div>
          <div style={styles.salesmanInfo}>
            <div style={styles.salesmanName}>{account.salesmanName}</div>
            <div style={styles.salesmanPhone}>
              {account.salesmanPhone ? (
                <a href={`tel:${account.salesmanPhone}`} style={{ color: PRIMARY, textDecoration: 'none' }}>
                  {account.salesmanPhone}
                </a>
              ) : '-'}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.account.sectionDeliveryDays')}</div>
        <div style={styles.daysRow}>
          {DAYS.map((day) => {
            const active = deliveryDays.includes(day);
            return (
              <div
                key={day}
                style={{ ...styles.dayChip, ...(active ? styles.dayActive : styles.dayInactive) }}
              >
                {t(`wpa.account.weekdays.${day}`)}
              </div>
            );
          })}
          <div
            style={{
              ...styles.holidayChip,
              ...(hasHoliday
                ? { background: PRIMARY, color: '#fff' }
                : { background: '#f0f0f0', color: '#ccc' }),
            }}
          >
            {t('wpa.account.holiday')}
          </div>
        </div>
      </div>

      <div style={{ height: 24 }} />
    </WpaLayout>
  );
}
